import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Loader2, 
  Users, 
  FileCheck, 
  Shield, 
  TrendingUp,
  DollarSign,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Eye,
  Search,
  Clock,
  Activity,
  Building2,
  Truck,
  Ban,
  RefreshCw,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('fleetflow_token')}`,
  'Content-Type': 'application/json'
});

const AdminPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingKyc, setPendingKyc] = useState([]);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showKycModal, setShowKycModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const loadAdminData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load platform stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats?period_days=30`, {
        headers: getHeaders()
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      // Load pending KYC documents
      const kycRes = await fetch(`${API_URL}/api/admin/kyc/pending`, {
        headers: getHeaders()
      });
      if (kycRes.ok) {
        const kycData = await kycRes.json();
        setPendingKyc(kycData.documents || []);
      }
      
      // Load fraud alerts
      const fraudRes = await fetch(`${API_URL}/api/fraud/pending-reviews`, {
        headers: getHeaders()
      });
      if (fraudRes.ok) {
        const fraudData = await fraudRes.json();
        setFraudAlerts(fraudData.reviews || []);
      }
      
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  const searchUsers = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/users?limit=50`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (roleFilter) url += `&role=${roleFilter}`;
      
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    }
  }, [searchQuery, roleFilter]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  useEffect(() => {
    if (activeTab === 'users') {
      searchUsers();
    }
  }, [activeTab, searchQuery, roleFilter, searchUsers]);

  const handleVerifyKyc = async (documentId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/kyc/${documentId}/verify`, {
        method: 'POST',
        headers: getHeaders()
      });
      
      if (res.ok) {
        toast.success('Document vérifié avec succès');
        setShowKycModal(false);
        loadAdminData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la vérification');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleRejectKyc = async (documentId) => {
    if (!rejectReason.trim()) {
      toast.error('Veuillez indiquer une raison de rejet');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/kyc/${documentId}/reject?reason=${encodeURIComponent(rejectReason)}`, {
        method: 'POST',
        headers: getHeaders()
      });
      
      if (res.ok) {
        toast.success('Document rejeté');
        setShowKycModal(false);
        setRejectReason('');
        loadAdminData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors du rejet');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleBlockUser = async (userId, block) => {
    const reason = block ? prompt('Raison du blocage:') : null;
    if (block && !reason) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/admin/users/${userId}/status?is_blocked=${block}&block_reason=${encodeURIComponent(reason || '')}`,
        { method: 'PUT', headers: getHeaders() }
      );
      
      if (res.ok) {
        toast.success(block ? 'Utilisateur bloqué' : 'Utilisateur débloqué');
        searchUsers();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getKycStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      verified: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      not_started: 'bg-slate-100 text-slate-700'
    };
    const labels = {
      pending: 'En attente',
      verified: 'Vérifié',
      rejected: 'Rejeté',
      not_started: 'Non démarré'
    };
    return <Badge className={styles[status] || styles.pending}>{labels[status] || status}</Badge>;
  };

  const getRiskBadge = (level) => {
    const styles = {
      low: 'bg-emerald-100 text-emerald-700',
      medium: 'bg-amber-100 text-amber-700',
      high: 'bg-orange-100 text-orange-700',
      critical: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[level] || styles.low}>{level?.toUpperCase()}</Badge>;
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      identity_card: 'Pièce d\'identité',
      passport: 'Passeport',
      kbis: 'Extrait Kbis',
      insurance_rc_pro: 'Assurance RC Pro',
      transport_license: 'Licence de transport'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
              Administration
            </h1>
            <p className="text-slate-600 mt-1">Gestion de la plateforme Flitflow</p>
          </div>
          <Button onClick={loadAdminData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Utilisateurs</p>
                  <p className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900">
                    {stats?.users?.total || 0}
                  </p>
                  <p className="text-xs text-emerald-600">+{stats?.users?.new || 0} ce mois</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Volume (30j)</p>
                  <p className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900">
                    {(stats?.financials?.volume || 0).toLocaleString()}€
                  </p>
                  <p className="text-xs text-slate-500">{stats?.financials?.transaction_count || 0} transactions</p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">KYC en attente</p>
                  <p className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900">
                    {stats?.kyc?.pending || 0}
                  </p>
                  <p className="text-xs text-emerald-600">{stats?.kyc?.verified || 0} vérifiés</p>
                </div>
                <div className={`w-12 h-12 ${(stats?.kyc?.pending || 0) > 0 ? 'bg-amber-100' : 'bg-slate-100'} rounded-lg flex items-center justify-center`}>
                  <FileCheck className={`w-6 h-6 ${(stats?.kyc?.pending || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Alertes Fraude</p>
                  <p className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900">
                    {fraudAlerts.length}
                  </p>
                </div>
                <div className={`w-12 h-12 ${fraudAlerts.length > 0 ? 'bg-red-100' : 'bg-slate-100'} rounded-lg flex items-center justify-center`}>
                  <AlertTriangle className={`w-6 h-6 ${fraudAlerts.length > 0 ? 'text-red-600' : 'text-slate-400'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 max-w-2xl gap-1">
            <TabsTrigger value="overview" className="text-xs md:text-sm">
              <TrendingUp className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="kyc" className="text-xs md:text-sm">
              <FileCheck className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">KYC</span> ({pendingKyc.length})
            </TabsTrigger>
            <TabsTrigger value="fraud" className="text-xs md:text-sm">
              <Shield className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Fraude</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="text-xs md:text-sm">
              <Users className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Utilisateurs</span>
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* User Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Répartition des utilisateurs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-blue-600" />
                        <span>Entreprises</span>
                      </div>
                      <span className="font-mono font-bold">{stats?.users?.enterprises || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Truck className="w-5 h-5 text-orange-600" />
                        <span>Transporteurs</span>
                      </div>
                      <span className="font-mono font-bold">{stats?.users?.carriers || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Mission Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Missions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Total</span>
                      <span className="font-mono font-bold">{stats?.missions?.total || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Nouvelles (30j)</span>
                      <span className="font-mono font-bold text-emerald-600">+{stats?.missions?.new || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Terminées</span>
                      <span className="font-mono font-bold">{stats?.missions?.completed || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Taux de complétion</span>
                      <Badge variant="outline" className="font-mono">
                        {stats?.missions?.completion_rate || 0}%
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Financial Summary */}
            <Card className="bg-slate-900 text-white">
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-slate-400 text-sm">Volume transactionnel</p>
                    <p className="text-3xl font-bold font-['JetBrains_Mono'] mt-1">
                      {(stats?.financials?.volume || 0).toLocaleString()}€
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Commissions perçues</p>
                    <p className="text-3xl font-bold font-['JetBrains_Mono'] mt-1 text-emerald-400">
                      {(stats?.financials?.fees_collected || 0).toLocaleString()}€
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Transactions</p>
                    <p className="text-3xl font-bold font-['JetBrains_Mono'] mt-1 text-orange-400">
                      {stats?.financials?.transaction_count || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* KYC Tab */}
          <TabsContent value="kyc" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Documents KYC en attente de validation</CardTitle>
                <CardDescription>Validez ou rejetez les documents soumis par les utilisateurs</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingKyc.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-300" />
                    <p>Aucun document en attente de validation</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingKyc.map((doc) => (
                      <div key={doc.id} className="p-4 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              {getKycStatusBadge(doc.status)}
                              <span className="font-medium">{getDocumentTypeLabel(doc.document_type)}</span>
                            </div>
                            <p className="text-sm text-slate-600">
                              {doc.user?.company_name} ({doc.user?.email})
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Soumis le {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedDocument(doc);
                                setShowKycModal(true);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Examiner
                            </Button>
                            <Button
                              size="sm"
                              className="bg-emerald-500 hover:bg-emerald-600"
                              onClick={() => handleVerifyKyc(doc.id)}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedDocument(doc);
                                setShowKycModal(true);
                              }}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fraud Tab */}
          <TabsContent value="fraud" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-orange-500" />
                  Alertes de fraude
                </CardTitle>
                <CardDescription>Transactions nécessitant une vérification manuelle</CardDescription>
              </CardHeader>
              <CardContent>
                {fraudAlerts.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-300" />
                    <p>Aucune alerte de fraude</p>
                    <p className="text-sm mt-2">Toutes les transactions sont en ordre</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {fraudAlerts.map((alert) => (
                      <div key={alert.id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              {getRiskBadge(alert.risk_level)}
                              <span className="text-sm text-slate-500">
                                Score: {alert.risk_score}
                              </span>
                            </div>
                            <p className="font-medium mt-2">
                              Transaction de {alert.amount}€
                            </p>
                            <p className="text-sm text-slate-500 mt-1">
                              {new Date(alert.analyzed_at).toLocaleString('fr-FR')}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline">
                              <Eye className="w-4 h-4 mr-1" />
                              Détails
                            </Button>
                            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600">
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Approuver
                            </Button>
                          </div>
                        </div>
                        {alert.alerts?.length > 0 && (
                          <div className="mt-3 space-y-1">
                            {alert.alerts.map((a, i) => (
                              <p key={i} className="text-sm text-amber-600 flex items-center gap-2">
                                <AlertTriangle className="w-3 h-3" />
                                {a.message}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle>Gestion des utilisateurs</CardTitle>
                    <CardDescription>Rechercher et gérer les comptes utilisateurs</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="Email ou entreprise..."
                        className="pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <select
                      className="border rounded-md px-3 py-2 text-sm"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                    >
                      <option value="">Tous les rôles</option>
                      <option value="enterprise">Entreprises</option>
                      <option value="carrier">Transporteurs</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Aucun utilisateur trouvé</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-3 font-medium text-slate-600">Utilisateur</th>
                          <th className="pb-3 font-medium text-slate-600">Rôle</th>
                          <th className="pb-3 font-medium text-slate-600">Missions</th>
                          <th className="pb-3 font-medium text-slate-600">Revenus</th>
                          <th className="pb-3 font-medium text-slate-600">KYC</th>
                          <th className="pb-3 font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id} className="border-b last:border-0 hover:bg-slate-50">
                            <td className="py-4">
                              <div>
                                <p className="font-medium">{user.company_name}</p>
                                <p className="text-sm text-slate-500">{user.email}</p>
                              </div>
                            </td>
                            <td className="py-4">
                              <Badge variant="outline">
                                {user.role === 'enterprise' ? 'Entreprise' : 'Transporteur'}
                              </Badge>
                            </td>
                            <td className="py-4 font-mono">{user.missions_count || 0}</td>
                            <td className="py-4 font-mono">{(user.total_revenue || 0).toLocaleString()}€</td>
                            <td className="py-4">
                              {getKycStatusBadge(user.kyc_status || 'not_started')}
                            </td>
                            <td className="py-4">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setShowUserModal(true);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {user.is_blocked ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleBlockUser(user.id, false)}
                                  >
                                    Débloquer
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleBlockUser(user.id, true)}
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* KYC Document Modal */}
        <Dialog open={showKycModal} onOpenChange={setShowKycModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Examen du document KYC</DialogTitle>
              <DialogDescription>
                {selectedDocument && getDocumentTypeLabel(selectedDocument.document_type)}
              </DialogDescription>
            </DialogHeader>
            {selectedDocument && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Entreprise</p>
                  <p className="font-medium">{selectedDocument.user?.company_name}</p>
                  <p className="text-sm text-slate-500">{selectedDocument.user?.email}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Nom du fichier</p>
                  <p className="font-medium">{selectedDocument.document_name}</p>
                </div>
                {selectedDocument.expiry_date && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Date d&apos;expiration</p>
                    <p className="font-medium">{new Date(selectedDocument.expiry_date).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Raison du rejet (si applicable)</label>
                  <Input
                    placeholder="Document illisible, expiré, etc."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowKycModal(false)}>
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleRejectKyc(selectedDocument?.id)}
                disabled={!rejectReason.trim()}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Rejeter
              </Button>
              <Button
                className="bg-emerald-500 hover:bg-emerald-600"
                onClick={() => handleVerifyKyc(selectedDocument?.id)}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Valider
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Detail Modal */}
        <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Détails de l&apos;utilisateur</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Entreprise</p>
                    <p className="font-medium">{selectedUser.company_name}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-medium text-sm">{selectedUser.email}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Rôle</p>
                    <p className="font-medium">{selectedUser.role === 'enterprise' ? 'Entreprise' : 'Transporteur'}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Abonnement</p>
                    <p className="font-medium capitalize">{selectedUser.subscription_plan || 'free'}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Missions</p>
                    <p className="font-medium font-mono">{selectedUser.missions_count || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Revenus totaux</p>
                    <p className="font-medium font-mono">{(selectedUser.total_revenue || 0).toLocaleString()}€</p>
                  </div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Inscrit le</p>
                  <p className="font-medium">{new Date(selectedUser.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                {selectedUser.is_blocked && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-600 font-medium">Utilisateur bloqué</p>
                    <p className="text-sm text-red-500">{selectedUser.block_reason}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUserModal(false)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminPage;
